import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type { PerfilComprador } from "@shared/schema";

/** Os dois perfis, com a frase que os distingue sem jargão. */
const OPCOES_PERFIL: Array<{ id: PerfilComprador; titulo: string; ajuda: string }> = [
  { id: "CORRETOR", titulo: "Sou corretor", ajuda: "Compro e revendo" },
  { id: "DISTRIBUIDOR", titulo: "Sou distribuidor", ajuda: "Compro para o meu negócio" },
];
import type { PreviaClaim } from "@shared/mercado";

/**
 * O corretor cria a conta a partir do link que recebeu. Rota PÚBLICA, fora do gate.
 *
 * ⚠️ O login dele é o TELEFONE — o mesmo número para onde o produtor mandou a nota.
 * Por isso o campo não existe neste formulário: ele vem do compartilhamento, e
 * deixá-lo editável abriria criar conta para um número que ninguém compartilhou.
 * O e-mail é opcional e serve só para recuperação.
 */
export default function CriarAcessoPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();

  const { data: previa, isLoading, isError } = useQuery<PreviaClaim>({
    queryKey: ["/api/mercado/publico", token, "claim"],
    queryFn: () => fetchJson<PreviaClaim>(`/api/mercado/publico/${token}/claim`),
    enabled: !!token,
  });

  const [nome, setNome] = useState("");
  const [senha, setSenha] = useState("");
  const [email, setEmail] = useState("");
  // Sem default: "corretor" e "distribuidor" são negócios diferentes, e adivinhar
  // aqui carimbaria metade da base errada no cadastro do produtor. O botão só
  // libera depois da escolha.
  const [perfil, setPerfil] = useState<PerfilComprador | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // `previa.nomeSugerido` só chega depois da query; sem `??` o campo nasceria vazio
  // e o produtor teria digitado o nome do corretor à toa.
  const nomeFinal = nome || previa?.nomeSugerido || "";

  async function handleCriar() {
    setErro(null);
    setSalvando(true);
    try {
      await apiRequest("POST", `/api/mercado/publico/${token}/claim`, {
        nome: nomeFinal.trim(),
        senha,
        email: email.trim() || undefined,
        perfil,
      });
      // O POST já devolve o cookie de sessão: mandar para o login logo depois de
      // escolher a senha seria perder gente no degrau mais bobo do funil.
      window.location.replace("/mercado");
    } catch (e: any) {
      setErro(e?.message ?? "Não foi possível criar o acesso.");
    } finally {
      setSalvando(false);
    }
  }

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center p-6 text-sm text-muted-foreground">Carregando…</div>;
  }

  // Revogado, expirado e inexistente chegam como o MESMO 404 — a tela não adivinha.
  if (isError || !previa) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-bold">Este link não está mais disponível.</p>
        <p className="text-sm text-muted-foreground max-w-xs">Peça um link novo para quem enviou o romaneio.</p>
      </div>
    );
  }

  if (previa.jaTemConta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="font-bold">Você já tem acesso.</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Entre com o seu telefone {previa.telefoneMascarado} e a senha que você cadastrou.
        </p>
        <Button onClick={() => setLocation("/login")}>Entrar</Button>
      </div>
    );
  }

  const podeCriar = nomeFinal.trim().length >= 2 && senha.length >= 6 && !!perfil && !salvando;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-md p-4 space-y-4">
        <Button variant="ghost" className="w-fit" onClick={() => setLocation(`/v/${token}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />Voltar para a nota
        </Button>

        <header className="space-y-1">
          <h1 className="text-xl font-extrabold">Criar seu acesso</h1>
          <p className="text-sm text-muted-foreground">
            {previa.romaneios === 1
              ? "Você tem 1 romaneio esperando."
              : `Você tem ${previa.romaneios} romaneios esperando`}
            {previa.romaneios > 1 && previa.produtores > 1
              ? `, de ${previa.produtores} produtores.`
              : previa.romaneios > 1 ? "." : ""}
          </p>
        </header>

        <div className="rounded-xl border bg-background p-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Seu telefone</Label>
            {/* Não editável: é o número para onde o romaneio foi enviado, e é ele que
                vira o seu login. */}
            <p className="text-sm font-semibold">{previa.telefoneMascarado}</p>
            <p className="text-xs text-muted-foreground">
              É este número que vai ser o seu login. Ele veio do romaneio que te mandaram.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="claim-nome">Seu nome</Label>
            <Input
              id="claim-nome"
              value={nomeFinal}
              onChange={e => setNome(e.target.value)}
              placeholder="Como você quer ser chamado"
              data-testid="input-claim-nome"
            />
          </div>

          {/* Duas opções grandes, não um select: é a única pergunta desta tela que
              o comprador precisa PARAR para responder, e no celular um select
              esconde a segunda opção atrás de um toque. */}
          <div className="space-y-1.5">
            <Label>Você é…</Label>
            <div className="grid grid-cols-2 gap-2">
              {OPCOES_PERFIL.map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setPerfil(o.id)}
                  className={`rounded-xl border-2 p-3 text-left transition-colors ${
                    perfil === o.id ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/40"
                  }`}
                  aria-pressed={perfil === o.id}
                  data-testid={`button-perfil-${o.id.toLowerCase()}`}
                >
                  <span className="block text-sm font-semibold">{o.titulo}</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">{o.ajuda}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="claim-senha">Crie uma senha</Label>
            <Input
              id="claim-senha"
              type="password"
              value={senha}
              onChange={e => setSenha(e.target.value)}
              placeholder="No mínimo 6 caracteres"
              data-testid="input-claim-senha"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="claim-email">E-mail (opcional)</Label>
            <Input
              id="claim-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Só para recuperar a senha"
              data-testid="input-claim-email"
            />
          </div>

          {erro && <p className="text-xs text-erro" data-testid="erro-claim">{erro}</p>}

          <Button className="w-full h-12" onClick={handleCriar} disabled={!podeCriar} data-testid="button-criar-acesso">
            {salvando ? "Criando…" : "Criar acesso"}
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground text-center pb-6">
          Seu acesso é gratuito. Você vê os romaneios que mandaram para você — nada mais da fazenda.
        </p>
      </div>
    </div>
  );
}
